import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Modal } from "react-bootstrap";
import { useAuth } from "../context/AuthContext.js";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, addDoc, query, where, getDocs } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase.js";
import { FileText, CheckCircle, ArrowLeft, Download, Pen, Trash2, ZoomIn, ZoomOut, Move } from "lucide-react";

// PDF libraries
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument } from 'pdf-lib';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Backend URL for PDF proxy
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.aitomize.space';

export default function DocumentViewerPage() {
  const { documentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Document states
  const [document, setDocument] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [proxyPdfUrl, setProxyPdfUrl] = useState(null);
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // PDF viewer states
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const [pdfLoadError, setPdfLoadError] = useState(false);

  // Signature states
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().split('T')[0]);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);

  // Draggable position states (percentages relative to page)
  const [signaturePos, setSignaturePos] = useState({ xPercent: 10, yPercent: 85 });
  const [datePos, setDatePos] = useState({ xPercent: 60, yPercent: 87 });
  const [dragging, setDragging] = useState(null); // 'signature' | 'date' | null
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Size config (percentages)
  const SIGNATURE_SIZE = { widthPercent: 25, heightPercent: 6 };
  const DATE_SIZE = { widthPercent: 15, heightPercent: 3 };

  // Refs
  const sigCanvas = useRef(null);
  const pdfContainerRef = useRef(null);
  const pageRef = useRef(null);

  // Check if current page is the signature page (last page)
  const isSignaturePage = numPages && currentPage === numPages;

  useEffect(() => {
    loadDocument();
  }, [documentId, user]);

  const loadDocument = async () => {
    if (!user?.uid || !documentId) return;

    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, "healthSafetyDocuments", documentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setError("Documento não encontrado.");
        setLoading(false);
        return;
      }

      const docData = { id: docSnap.id, ...docSnap.data() };
      setDocument(docData);

      let originalUrl = null;
      if (docData.downloadUrl) {
        originalUrl = docData.downloadUrl.replace(/[\n\r\s]/g, '');
      } else {
        const storagePath = `health-safety/${docData.fileName}`;
        const storageRef = ref(storage, storagePath);
        originalUrl = await getDownloadURL(storageRef);
      }

      setPdfUrl(originalUrl);
      setProxyPdfUrl(`${BACKEND_URL}/api/pdf-proxy?url=${encodeURIComponent(originalUrl)}`);

      // Check existing signature
      const sigQuery = query(
        collection(db, "documentSignatures"),
        where("userId", "==", user.uid),
        where("documentId", "==", documentId),
        where("documentVersion", "==", docData.version)
      );
      const sigSnap = await getDocs(sigQuery);
      if (!sigSnap.empty) {
        setSignature({ id: sigSnap.docs[0].id, ...sigSnap.docs[0].data() });
      }

    } catch (err) {
      console.error("Error loading document:", err);
      setError("Erro ao carregar documento.");
    } finally {
      setLoading(false);
    }
  };

  // Handle tap on mobile - opens modal
  const handleTap = useCallback((type) => {
    if (isDragging) return; // Don't open if was dragging
    if (type === 'signature') {
      setShowSignatureModal(true);
    } else {
      setShowDateModal(true);
    }
  }, [isDragging]);

  // Handle long press start (for dragging on mobile)
  const handleTouchStart = useCallback((e, type) => {
    const clientX = e.touches[0].clientX;
    const clientY = e.touches[0].clientY;
    const element = e.currentTarget;
    const rect = element.getBoundingClientRect();

    setIsDragging(false);

    // Start long press timer (300ms)
    const timer = setTimeout(() => {
      setIsDragging(true);
      setDragOffset({
        x: clientX - rect.left,
        y: clientY - rect.top
      });
      setDragging(type);
      // Vibrate to indicate drag mode (if supported)
      if (navigator.vibrate) navigator.vibrate(50);
    }, 300);

    setLongPressTimer(timer);
  }, []);

  // Handle touch end
  const handleTouchEnd = useCallback((type) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    if (!isDragging) {
      // Was a tap, not a drag - open modal
      handleTap(type);
    }
    setDragging(null);
    setTimeout(() => setIsDragging(false), 100);
  }, [longPressTimer, isDragging, handleTap]);

  // Mouse drag start (desktop)
  const handleMouseDown = useCallback((e, type) => {
    e.preventDefault();
    const element = e.currentTarget;
    const rect = element.getBoundingClientRect();

    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setDragging(type);
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!dragging || !pageRef.current) return;

    // Only prevent default on mouse events, not touch (passive listener)
    if (e.type === 'mousemove') {
      e.preventDefault();
    }
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Use actual DOM element dimensions (getBoundingClientRect)
    const pageRect = pageRef.current.getBoundingClientRect();

    // Calculate new position as percentage using ACTUAL container size
    let newX = ((clientX - pageRect.left - dragOffset.x) / pageRect.width) * 100;
    let newY = ((clientY - pageRect.top - dragOffset.y) / pageRect.height) * 100;

    // Clamp to bounds
    newX = Math.max(0, Math.min(85, newX));
    newY = Math.max(0, Math.min(95, newY));

    if (dragging === 'signature') {
      setSignaturePos({ xPercent: newX, yPercent: newY });
    } else if (dragging === 'date') {
      setDatePos({ xPercent: newX, yPercent: newY });
    }
  }, [dragging, dragOffset]);

  const handleDragEnd = useCallback(() => {
    setDragging(null);
    setTimeout(() => setIsDragging(false), 100);
  }, []);

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [dragging, handleDragMove, handleDragEnd]);

  // Auto-adjust scale for mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setScale(0.6); // Smaller scale for mobile
    }
  }, []);

  // Update container size when scale changes
  useEffect(() => {
    setTimeout(() => {
      if (pageRef.current) {
        const rect = pageRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    }, 100);
  }, [scale]);

  // Generate signed PDF
  const generateSignedPDF = async () => {
    const response = await fetch(proxyPdfUrl);
    if (!response.ok) throw new Error('Failed to fetch PDF');
    const existingPdfBytes = await response.arrayBuffer();

    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    // Get signature image
    const signatureResponse = await fetch(signatureDataUrl);
    const signatureImageBytes = await signatureResponse.arrayBuffer();
    const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

    // Get last page
    const pages = pdfDoc.getPages();
    const targetPage = pages[pages.length - 1];
    const { width: pageWidth, height: pageHeight } = targetPage.getSize();

    // Debug log the positions
    console.log('=== PDF Generation Debug ===');
    console.log('PDF Page Size:', { pageWidth, pageHeight });
    console.log('Signature Position (%):', signaturePos);
    console.log('Date Position (%):', datePos);
    console.log('Container Size:', containerSize);

    // Calculate positions from percentages (PDF coordinates start from bottom-left)
    const sigX = (signaturePos.xPercent / 100) * pageWidth;
    const sigY = pageHeight - ((signaturePos.yPercent / 100) * pageHeight) - ((SIGNATURE_SIZE.heightPercent / 100) * pageHeight);
    const sigWidth = (SIGNATURE_SIZE.widthPercent / 100) * pageWidth;
    const sigHeight = (SIGNATURE_SIZE.heightPercent / 100) * pageHeight;

    const dateX = (datePos.xPercent / 100) * pageWidth;
    const dateY = pageHeight - ((datePos.yPercent / 100) * pageHeight) - 12; // Adjust for text baseline

    console.log('Calculated PDF Coordinates:');
    console.log('  Signature: x=', sigX.toFixed(2), 'y=', sigY.toFixed(2), 'w=', sigWidth.toFixed(2), 'h=', sigHeight.toFixed(2));
    console.log('  Date: x=', dateX.toFixed(2), 'y=', dateY.toFixed(2));

    // Draw signature
    targetPage.drawImage(signatureImage, {
      x: sigX,
      y: sigY,
      width: sigWidth,
      height: sigHeight,
    });

    // Draw date
    const [year, month, day] = signatureDate.split('-');
    targetPage.drawText(`${day}/${month}/${year}`, {
      x: dateX,
      y: dateY,
      size: 11,
    });

    return await pdfDoc.save();
  };

  // Handle document signing
  const handleSignDocument = async () => {
    if (!signatureDataUrl) {
      alert("Por favor, adicione sua assinatura clicando no campo de assinatura.");
      return;
    }

    try {
      setUploading(true);
      const signedPdfBytes = await generateSignedPDF();

      // Convert to base64
      const uint8Array = new Uint8Array(signedPdfBytes);
      let binary = '';
      for (let i = 0; i < uint8Array.length; i++) {
        binary += String.fromCharCode(uint8Array[i]);
      }
      const fullBase64 = `data:application/pdf;base64,${btoa(binary)}`;

      // Save in chunks
      const CHUNK_SIZE = 900000;
      const totalChunks = Math.ceil(fullBase64.length / CHUNK_SIZE);
      const signatureId = `sig_${document.id}_${user.uid}_${Date.now()}`;

      await addDoc(collection(db, "documentSignatures"), {
        userId: user.uid,
        userName: user.displayName || user.name || user.email,
        userEmail: user.email,
        documentId: document.id,
        documentVersion: document.version,
        documentTitle: document.title?.pt || document.title?.en || "Documento",
        signedAt: new Date(),
        signatureDate: signatureDate,
        signaturePosition: signaturePos,
        datePosition: datePos,
        originalFileName: `${document.title?.en || 'document'}_signed.pdf`,
        fileSize: signedPdfBytes.byteLength,
        totalChunks,
        signatureId,
        status: "signed",
        signatureMethod: "digital"
      });

      for (let i = 0; i < totalChunks; i++) {
        const chunk = fullBase64.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        await addDoc(collection(db, "documentChunks"), {
          signatureId,
          chunkIndex: i,
          chunkData: chunk,
          createdAt: new Date()
        });
      }

      await loadDocument();
      alert("Documento assinado com sucesso!");

    } catch (err) {
      console.error("Signature error:", err);
      alert("Erro ao assinar documento.");
    } finally {
      setUploading(false);
    }
  };

  // Save signature from modal
  const saveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setSignatureDataUrl(sigCanvas.current.toDataURL('image/png'));
      setShowSignatureModal(false);
    } else {
      alert("Por favor, desenhe sua assinatura.");
    }
  };

  // PDF handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoadError(false);
    setCurrentPage(numPages); // Go to last page
  };

  const onPageLoadSuccess = (page) => {
    setPageDimensions({ width: page.width, height: page.height });
    // Update container size after render
    setTimeout(() => {
      if (pageRef.current) {
        const rect = pageRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    }, 100);
  };

  // Calculate overlay styles - use containerSize (real DOM dimensions)
  const getOverlayStyle = (pos, size, type, isDraggingThis) => {
    // Use containerSize from getBoundingClientRect, fallback to calculated
    const actualWidth = containerSize.width || (pageDimensions.width * scale);
    const actualHeight = containerSize.height || (pageDimensions.height * scale);

    if (!actualWidth) return { display: 'none' };

    const baseStyle = {
      position: 'absolute',
      left: `${(pos.xPercent / 100) * actualWidth}px`,
      top: `${(pos.yPercent / 100) * actualHeight}px`,
      cursor: isDraggingThis ? 'grabbing' : 'grab',
      userSelect: 'none',
      touchAction: 'none',
      transition: isDraggingThis ? 'none' : 'box-shadow 0.2s',
      boxShadow: isDraggingThis ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 6px rgba(0,0,0,0.15)',
      zIndex: isDraggingThis ? 100 : 10,
    };

    if (type === 'signature') {
      return {
        ...baseStyle,
        width: `${(size.widthPercent / 100) * actualWidth}px`,
        height: `${(size.heightPercent / 100) * actualHeight}px`,
        border: signatureDataUrl ? '2px solid #28a745' : '2px dashed #007bff',
        backgroundColor: signatureDataUrl ? 'rgba(40, 167, 69, 0.15)' : 'rgba(0, 123, 255, 0.15)',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    } else {
      return {
        ...baseStyle,
        padding: '6px 14px',
        border: '2px solid #28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.15)',
        borderRadius: '4px',
        fontSize: `${Math.max(11, 12 * scale)}px`,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      };
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Carregando documento...</p>
        </div>
      </Container>
    );
  }

  if (error || !document) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error || "Documento não encontrado."}</Alert>
        <Button variant="primary" onClick={() => navigate("/health-safety")}>
          <ArrowLeft size={18} className="me-2" />Voltar
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-3 px-2 px-md-4">
      {/* Header */}
      <Row className="mb-2">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate("/health-safety")}
            className="mb-2"
          >
            <ArrowLeft size={16} className="me-1" />Voltar
          </Button>
          <h5 className="fw-bold mb-0">
            <FileText className="me-2" size={20} />
            {document.title?.pt || document.title?.en}
          </h5>
        </Col>
      </Row>

      {/* Already Signed */}
      {signature && (
        <Alert variant="success" className="py-2 mb-2">
          <CheckCircle size={16} className="me-2" />
          <strong>Assinado</strong> em {signature.signedAt?.toDate?.().toLocaleString("pt-BR")}
          {signature.signatureId && (
            <Button
              variant="link"
              size="sm"
              className="ms-2 p-0"
              onClick={async () => {
                const chunksSnap = await getDocs(query(
                  collection(db, "documentChunks"),
                  where("signatureId", "==", signature.signatureId)
                ));
                const chunks = chunksSnap.docs.map(d => d.data()).sort((a, b) => a.chunkIndex - b.chunkIndex);
                const link = window.document.createElement('a');
                link.href = chunks.map(c => c.chunkData).join('');
                link.download = signature.originalFileName || 'documento_assinado.pdf';
                link.click();
              }}
            >
              <Download size={14} /> Baixar
            </Button>
          )}
        </Alert>
      )}

      {/* PDF Viewer */}
      <Card className="shadow-sm">
        {/* Toolbar */}
        <Card.Header className="bg-dark text-white py-2">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-1">
              <Button variant="outline-light" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>←</Button>
              <span className="mx-1 small">Pág {currentPage}/{numPages || '?'}</span>
              <Button variant="outline-light" size="sm" onClick={() => setCurrentPage(p => Math.min(numPages || p, p + 1))} disabled={currentPage >= numPages}>→</Button>
            </div>

            <div className="d-flex align-items-center gap-1">
              <Button variant="outline-light" size="sm" onClick={() => setScale(s => Math.max(0.5, s - 0.2))}><ZoomOut size={14} /></Button>
              <span className="small">{Math.round(scale * 100)}%</span>
              <Button variant="outline-light" size="sm" onClick={() => setScale(s => Math.min(2, s + 0.2))}><ZoomIn size={14} /></Button>
            </div>

            {!signature && (
              <Button
                variant="success"
                size="sm"
                onClick={handleSignDocument}
                disabled={!signatureDataUrl || uploading}
              >
                {uploading ? <Spinner size="sm" /> : <><Pen size={14} className="me-1" />Finalizar</>}
              </Button>
            )}
          </div>
        </Card.Header>

        {/* PDF */}
        <Card.Body className="p-2 overflow-auto" style={{ backgroundColor: '#525659', maxHeight: '75vh' }} ref={pdfContainerRef}>
          {proxyPdfUrl && !pdfLoadError ? (
            <div className="d-flex justify-content-center">
              <Document
                file={proxyPdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={() => setPdfLoadError(true)}
                loading={<div className="text-center py-5"><Spinner variant="light" /><p className="text-light mt-2">Carregando...</p></div>}
              >
                <div ref={pageRef} style={{ position: 'relative', display: 'inline-block' }}>
                  <Page
                    pageNumber={currentPage}
                    scale={scale}
                    onLoadSuccess={onPageLoadSuccess}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />

                  {/* Draggable overlays - only on signature page when not signed */}
                  {isSignaturePage && !signature && (
                    <>
                      {/* Signature field */}
                      <div
                        style={getOverlayStyle(signaturePos, SIGNATURE_SIZE, 'signature', dragging === 'signature')}
                        onMouseDown={(e) => handleMouseDown(e, 'signature')}
                        onDoubleClick={() => setShowSignatureModal(true)}
                        onTouchStart={(e) => handleTouchStart(e, 'signature')}
                        onTouchEnd={() => handleTouchEnd('signature')}
                        title="Toque para assinar • Segure para arrastar"
                      >
                        {signatureDataUrl ? (
                          <img src={signatureDataUrl} alt="Assinatura" style={{ maxWidth: '100%', maxHeight: '100%', pointerEvents: 'none' }} />
                        ) : (
                          <span style={{ color: '#007bff', fontSize: `${Math.max(9, 10 * scale)}px`, pointerEvents: 'none' }}>
                            ✍️ Toque para assinar
                          </span>
                        )}
                      </div>

                      {/* Date field */}
                      <div
                        style={getOverlayStyle(datePos, DATE_SIZE, 'date', dragging === 'date')}
                        onMouseDown={(e) => handleMouseDown(e, 'date')}
                        onDoubleClick={() => setShowDateModal(true)}
                        onTouchStart={(e) => handleTouchStart(e, 'date')}
                        onTouchEnd={() => handleTouchEnd('date')}
                        title="Toque para editar • Segure para arrastar"
                      >
                        <span style={{ color: '#28a745', pointerEvents: 'none' }}>
                          📅 {signatureDate.split('-').reverse().join('/')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </Document>
            </div>
          ) : pdfLoadError ? (
            <iframe src={pdfUrl} title="PDF" style={{ width: '100%', height: '600px', border: 'none' }} />
          ) : (
            <Alert variant="warning">PDF não disponível.</Alert>
          )}
        </Card.Body>

        {/* Instructions */}
        {!signature && isSignaturePage && (
          <Card.Footer className="bg-light py-2">
            <small className="text-muted">
              📱 <strong>Toque</strong> para editar • <strong>Segure</strong> para arrastar • Clique <strong>Finalizar</strong> quando pronto
            </small>
          </Card.Footer>
        )}
      </Card>

      {/* Signature Modal */}
      <Modal show={showSignatureModal} onHide={() => setShowSignatureModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>✍️ Desenhe sua assinatura</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div
            className="border rounded bg-white"
            style={{
              touchAction: 'none',
              width: '100%',
              height: '180px'
            }}
          >
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                className: 'w-100 h-100',
                style: { touchAction: 'none', cursor: 'crosshair' }
              }}
              backgroundColor="white"
              penColor="black"
            />
          </div>
          <Button variant="outline-secondary" size="sm" className="mt-2" onClick={() => sigCanvas.current?.clear()}>
            <Trash2 size={14} className="me-1" />Limpar
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSignatureModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={saveSignature}>Confirmar</Button>
        </Modal.Footer>
      </Modal>

      {/* Date Modal */}
      <Modal show={showDateModal} onHide={() => setShowDateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>📅 Data do documento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="date"
            value={signatureDate}
            onChange={(e) => setSignatureDate(e.target.value)}
            size="lg"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowDateModal(false)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
